/**
 * `I18nProvider` + `useI18n()` — FR/NL/EN dictionaries, locale from the
 * signed-in user (once known) or the browser, persisted across sessions.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type { Locale } from '../types/domain'
import type { Dictionary, TranslationKey } from './types'
import en from './en'
import fr from './fr'
import nl from './nl'

const dictionaries: Record<Locale, Dictionary> = { fr, nl, en }
const SUPPORTED: Locale[] = ['fr', 'nl', 'en']
const STORAGE_KEY = 'pivot.locale'

function isLocale(value: string): value is Locale {
  return (SUPPORTED as string[]).includes(value)
}

function detectLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && isLocale(stored)) return stored
  } catch {
    // localStorage unavailable (private mode, SSR) — fall through.
  }
  const browser = navigator.language.slice(0, 2).toLowerCase()
  return isLocale(browser) ? browser : 'fr'
}

function resolve(dict: Dictionary, key: string): string {
  const parts = key.split('.')
  let node: unknown = dict
  for (const part of parts) {
    if (typeof node === 'object' && node !== null && part in node) {
      node = (node as Record<string, unknown>)[part]
    } else {
      return key
    }
  }
  return typeof node === 'string' ? node : key
}

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template
  return template.replace(/\{\{(\w+)\}\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  )
}

interface I18nContextValue {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string
}

const I18nContext = createContext<I18nContextValue | null>(null)

export function I18nProvider({
  children,
  initialLocale,
}: {
  children: ReactNode
  initialLocale?: Locale
}) {
  const [locale, setLocaleState] = useState<Locale>(() => initialLocale ?? detectLocale())

  // PIVOT-137: `<html lang>` starts hard-coded "en" in index.html (it can't
  // know the locale before this provider mounts) and was never corrected
  // after — WCAG 3.1.1, and it also meant a French session was indexed and
  // read aloud as English. Runs on mount too (not just on a later switch),
  // so a returning visitor whose stored locale isn't "en" gets the right
  // value as soon as React takes over, not only after they touch the switch.
  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // ignore storage failures (private mode)
    }
  }, [])

  const t = useCallback(
    (key: TranslationKey, vars?: Record<string, string | number>) =>
      interpolate(resolve(dictionaries[locale], key), vars),
    [locale],
  )

  const value = useMemo<I18nContextValue>(() => ({ locale, setLocale, t }), [locale, setLocale, t])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components -- hook, not a component
export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n must be used within an I18nProvider')
  return ctx
}

export const SUPPORTED_LOCALES = SUPPORTED
